import { api } from "./client";
import type { PagedResult } from "@/types/api";

/**
 * Communication Log v1.0 §8 — frontend client for the central
 * external-communication logbook. Mirrors the backend
 * <c>CommunicationsController</c> + <c>AdminCommunicationsController</c>.
 *
 * Behind feature flag <c>comm-log-flag</c> until PR 2 lands; the controller
 * throws 404 for everyone if the flag is off.
 */

export type CommunicationChannel =
  | "Call"
  | "Voicemail"
  | "Email"
  | "Sms"
  | "Meeting"
  /** A message submitted through somebody's web contact form — the Broker
   *  intro tab's web form card logs one per submit (D32). */
  | "WebForm";

export type CommunicationDirection = "Inbound" | "Outbound";

/**
 * Who the conversation was with, asked only when the entry is placed on a buyer
 * submission. Replaces the retired bespoke `BuyerConversationParticipant`; the
 * member names are identical, so the wire contract is unchanged.
 *
 * Lives on the ENTRY, while `packageEligible` lives on the PLACEMENT. The
 * counterparty is invariant across where an entry is filed — call the buyer
 * about two submissions and you called the buyer, twice over — so a per-placement
 * counterparty could hold two contradictory answers to one question. Eligibility
 * genuinely differs per placement. Asked together, stored apart.
 *
 * Distinct from `CommunicationParticipantDto.roleHint`, which is unchanged: that
 * is free-text display text, this is structured and gates buyer milestones.
 */
export type CommunicationCounterparty =
  | "Buyer"
  | "ListingAgent"
  | "Seller"
  | "Other";

export type CommunicationLogActivityType =
  | "Created"
  | "EditedField"
  | "PlacementAdded"
  | "PlacementRemoved"
  | "ParticipantAdded"
  | "ParticipantRemoved"
  | "SoftDeleted"
  | "Restored";

export interface CommunicationLogPlacementDto {
  id: string;
  ownerType: string;
  ownerId: string;
  displayLabel: string | null;
  detailUrl: string | null;
  /**
   * May this communication be used to write the package for THIS submission?
   * Per-placement: a note that belongs in listing B's package must not leak into
   * listing A's. Always false for owner types that produce no package.
   */
  packageEligible: boolean;
}

export interface CommunicationParticipantDto {
  id: string;
  contactId: string | null;
  displayName: string;
  phoneOrEmail: string | null;
  roleHint: string | null;
}

export interface CommunicationLogEntryDto {
  id: string;
  channel: CommunicationChannel;
  direction: CommunicationDirection | null;
  occurredAt: string;
  loggedByUserId: string;
  loggedByDisplayName: string;
  loggedAt: string;
  durationMinutes: number | null;
  subject: string | null;
  body: string;
  outcome: string | null;
  modality: string | null;
  emailFromAddress: string | null;
  emailToAddress: string | null;
  externalPhoneNumber: string | null;
  externalPartyName: string | null;
  createdAt: string;
  modifiedAt: string;
  isDeleted: boolean;
  placements: CommunicationLogPlacementDto[];
  participants: CommunicationParticipantDto[];
  /** Per-ENTRY. Null when the entry carries no buyer-submission placement. */
  counterpartyRole: CommunicationCounterparty | null;
}

export interface CommunicationLogActivityDto {
  id: string;
  entryId: string;
  actorUserId: string;
  actorDisplayName: string;
  activityType: CommunicationLogActivityType;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  placementOwnerType: string | null;
  placementOwnerId: string | null;
  createdAt: string;
}

export interface PlacementRefDto {
  ownerType: string;
  ownerId: string;
  /** Defaults to false server-side — a placement with no explicit choice is
   * never eligible. Only meaningful for `buyer_submission`. */
  packageEligible?: boolean;
}

/** Sets or clears a submission placement's package-eligibility. Audited. */
export interface SetPlacementEligibilityDto {
  ownerType: string;
  ownerId: string;
  packageEligible: boolean;
}

export interface CreateParticipantDto {
  displayName: string;
  phoneOrEmail?: string | null;
  roleHint?: string | null;
  contactId?: string | null;
}

export interface CreateCommunicationLogDto {
  channel: CommunicationChannel;
  direction?: CommunicationDirection | null;
  occurredAt: string;
  durationMinutes?: number | null;
  subject?: string | null;
  body: string;
  outcome?: string | null;
  modality?: string | null;
  emailFromAddress?: string | null;
  emailToAddress?: string | null;
  externalPhoneNumber?: string | null;
  externalPartyName?: string | null;
  placements: PlacementRefDto[];
  participants: CreateParticipantDto[];
  /** Accepted only when at least one placement is a buyer submission. */
  counterpartyRole?: CommunicationCounterparty | null;
}

export interface UpdateCommunicationLogDto {
  occurredAt?: string | null;
  durationMinutes?: number | null;
  subject?: string | null;
  body?: string | null;
  outcome?: string | null;
  modality?: string | null;
  emailFromAddress?: string | null;
  emailToAddress?: string | null;
  externalPhoneNumber?: string | null;
  externalPartyName?: string | null;
}

export interface AddParticipantDto {
  displayName: string;
  phoneOrEmail?: string | null;
  roleHint?: string | null;
  contactId?: string | null;
}

export interface CommunicationLogQuery {
  page?: number;
  pageSize?: number;
  channel?: CommunicationChannel;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  ownerType?: string;
  ownerId?: string;
  /**
   * The left rail's selected view — see `pages/communications/inboxScopes`.
   * The client sends whatever the URL holds and the SERVER normalises it, so an
   * unknown or unavailable value renders the caller's default view rather than
   * an error. `buildCommunicationsExportUrl` serialises every key of this
   * object, so the CSV export follows the selected view for free.
   */
  scope?: string;
}

export interface ContactSearchResultDto {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  source: string;
}

export const communicationsApi = {
  list: (query?: CommunicationLogQuery) =>
    api.get<PagedResult<CommunicationLogEntryDto>>("/communications", query),

  get: (id: string) =>
    api.get<CommunicationLogEntryDto>(`/communications/${id}`),

  create: (payload: CreateCommunicationLogDto) =>
    api.post<CommunicationLogEntryDto>("/communications", payload),

  update: (id: string, payload: UpdateCommunicationLogDto) =>
    api.patch<CommunicationLogEntryDto>(`/communications/${id}`, payload),

  delete: (id: string) => api.delete<void>(`/communications/${id}`),

  restore: (id: string) =>
    api.post<void>(`/communications/${id}/restore`),

  addPlacement: (id: string, payload: PlacementRefDto) =>
    api.post<CommunicationLogEntryDto>(
      `/communications/${id}/placements`,
      payload,
    ),

  setPlacementEligibility: (id: string, payload: SetPlacementEligibilityDto) =>
    api.post<CommunicationLogEntryDto>(
      `/communications/${id}/placements/eligibility`,
      payload,
    ),

  removePlacement: (id: string, ownerType: string, ownerId: string) =>
    api.delete<CommunicationLogEntryDto>(
      `/communications/${id}/placements/${ownerType}/${encodeURIComponent(ownerId)}`,
    ),

  addParticipant: (id: string, payload: AddParticipantDto) =>
    api.post<CommunicationLogEntryDto>(
      `/communications/${id}/participants`,
      payload,
    ),

  removeParticipant: (id: string, participantId: string) =>
    api.delete<CommunicationLogEntryDto>(
      `/communications/${id}/participants/${participantId}`,
    ),

  getActivity: (id: string, skip = 0, take = 50) =>
    api.get<PagedResult<CommunicationLogActivityDto>>(
      `/communications/${id}/activity`,
      { skip, take },
    ),

  listByOwner: (
    ownerType: string,
    ownerId: string,
    skip = 0,
    take = 50,
  ) =>
    api.get<CommunicationLogEntryDto[]>(
      `/communications/by-owner/${ownerType}/${encodeURIComponent(ownerId)}`,
      { skip, take },
    ),

  recentCountByOwner: (
    ownerType: string,
    ownerId: string,
    windowDays = 30,
  ) =>
    api.get<number>(
      `/communications/by-owner/${ownerType}/${encodeURIComponent(ownerId)}/recent-count`,
      { windowDays },
    ),

  myRecent: (limit = 5) =>
    api.get<CommunicationLogEntryDto[]>("/communications/my-recent", { limit }),

  searchContacts: (q: string, limit = 20) =>
    api.get<ContactSearchResultDto[]>("/communications/contacts/search", {
      q,
      limit,
    }),
};

/**
 * Build the admin CSV-export URL with the same query params the inbox
 * list endpoint accepts. Returned as a string so the caller wires it
 * to a native <a download> element — the lowest-friction download path
 * with cookie auth.
 */
export function buildCommunicationsExportUrl(
  query?: CommunicationLogQuery,
): string {
  const base =
    (import.meta.env.VITE_API_BASE_URL ?? "/api") +
    "/admin/communications/export";
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== "") {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// ----------------------------------------------------------------------
// Owner-type display helpers — UI uses these to label placement chips
// and the central inbox left-rail "Compliance view" entity-type filter.
// ----------------------------------------------------------------------

/**
 * Owner types valid for placement creation. **This array MUST equal the
 * backend's `CommunicationOwnerTypes.All` exactly**, and
 * `communications.ownerTypes.test.ts` reads the C# source and fails the build
 * if it does not.
 *
 * 🔴 <b>A short list here is not a missing option — it is a MIS-SAVED ROW.</b>
 * `PlacementsEditor` (`LogCommunicationModal.tsx`) renders a
 * `<Select value={row.ownerType} disabled={row.locked}>` from this array. When a
 * detail page opens the modal, its own record arrives as a LOCKED first
 * placement; if that owner type has no matching `<option>`, some browsers fire
 * `onChange` on the disabled control to the FIRST option — `"deal"` — and the
 * placement saves pointing at a record of one type while claiming to be another.
 * The label resolver then queries the wrong table and every chip reads
 * "(unavailable)". That is not hypothetical: migration
 * `20270701000000_CorrectMistypedDealPlacementsToLead` exists to repair exactly
 * these rows for `"lead"`, which was missing from this array after the Lead
 * detail page shipped.
 *
 * Eleven tokens were missing again — the four SPV-Pool service providers, TPS,
 * the three attach-record expansion types, and all three Buyer-programme types,
 * every one of which has a detail page that can open this modal.
 *
 * `pipeline_deal` is DELIBERATELY absent, here and in the backend `All`: it is
 * reserved until the kanban-card detail drawer ships (PR 11). No detail page can
 * open the modal against one, so no locked row can arrive as one.
 *
 * Order is the dropdown's order and is deliberately NOT sorted — `deal` stays
 * first because it always has been, and the newly-added tokens are appended in
 * the backend's own grouping so the two files read alike.
 */
export const COMMUNICATION_OWNER_TYPES = [
  "deal",
  "loan",
  "broker",
  "brokerage",
  "lead",
  "person",
  "entity",
  "business",
  "vendor",
  "spv",
  "spv_pool",
  "investor",
  "candidate",
  "borrower",
  "seller",
  // SPV Pool service-provider counterparties.
  "indenture_trustee",
  "trust_company",
  "placement_agent",
  "backup_servicer",
  // Third-Party Servicer.
  "tps",
  // Attach-record expansion.
  "local_enforcer",
  "portal_user",
  "reunderwriting-case",
  // Buyer CRM / Buyer programme. `buyer_lead` is the person shopping — NOT
  // `lead` (broker recruitment) and NOT the borrower-side party.
  "buyer_lead",
  "listing",
  "buyer_submission",
] as const;

export type CommunicationOwnerType =
  (typeof COMMUNICATION_OWNER_TYPES)[number];

/**
 * Display name per owner type. MUST cover every token in the server's
 * `CommunicationOwnerTypes.All` — an unmapped token falls through to the raw
 * snake_case string, so a chip reads "buyer_submission" and a sentence reads
 * "on this local_enforcer". That is a silent leak: nothing throws, nothing is
 * logged, and the map is not the allow-list, so nothing forces the two to agree.
 *
 * Names mirror the server's own chip prefixes (ResourceLabelService /
 * CommunicationOwnerLabelService) and RelatedConversationsPanel's TYPE_NOUNS,
 * so one record reads the same on every surface.
 *
 * `pipeline_deal` is here but deliberately NOT in `CommunicationOwnerTypes.All`
 * — it is reserved until the kanban-card detail drawer ships. A label for a
 * token that cannot yet be created is harmless; the reverse is not.
 */
const OWNER_TYPE_LABELS: Record<string, string> = {
  deal: "Deal",
  loan: "Loan",
  broker: "Broker",
  brokerage: "Brokerage",
  person: "Person",
  entity: "Entity",
  business: "Business",
  vendor: "Vendor",
  spv: "SPV",
  spv_pool: "SPV Pool",
  investor: "Investor",
  candidate: "Candidate",
  lead: "Lead",
  pipeline_deal: "Pipeline deal",
  borrower: "Borrower",
  seller: "Seller",
  // SPV Pool service-provider counterparties.
  indenture_trustee: "Indenture Trustee",
  trust_company: "Trust Company",
  placement_agent: "Placement Agent",
  backup_servicer: "Backup Servicer",
  // Third-Party Servicer.
  tps: "TPS",
  // Attach-record expansion.
  local_enforcer: "Local Enforcer",
  portal_user: "Portal Account",
  "reunderwriting-case": "Reunderwriting Case",
  // Buyer CRM / Buyer programme. `buyer_lead` is the person shopping — NOT
  // `lead` (broker recruitment) and NOT the borrower-side party.
  buyer_lead: "Buyer",
  listing: "Listing",
  buyer_submission: "Submission",
};

/**
 * Nouns that must NOT be produced by lower-casing the display label —
 * acronyms lose their meaning ("this spv yet", "this tps yet"). Only entries
 * that differ from `label.toLowerCase()` belong here.
 */
const OWNER_TYPE_SENTENCE_NOUNS: Record<string, string> = {
  spv: "SPV",
  spv_pool: "SPV pool",
  tps: "TPS",
};

/** Title-cased display name — for chips, headings and picker options. */
export function ownerTypeLabel(ownerType: string): string {
  return OWNER_TYPE_LABELS[ownerType] ?? ownerType;
}

/**
 * The same noun as it should read MID-SENTENCE ("…logged on this deal yet").
 *
 * Callers used to write `ownerTypeLabel(t).toLowerCase()`, which is right for
 * "Deal" and wrong for every acronym: "SPV" became "spv" and "TPS" became
 * "tps". The unknown-token fallback also un-mangles the raw slug rather than
 * dropping a snake_case string into prose.
 */
export function ownerTypeNoun(ownerType: string): string {
  const acronym = OWNER_TYPE_SENTENCE_NOUNS[ownerType];
  if (acronym) return acronym;
  const label = OWNER_TYPE_LABELS[ownerType];
  if (label) return label.toLowerCase();
  return ownerType.replace(/[_-]+/g, " ").trim() || ownerType;
}
